/**
 * Integrytas Group - Data Quality Engine (DQE)
 * "Review breaches" drill-through — lists the ACTUAL rows that currently violate
 * one rule. Read-only diagnostic view over the shared data layer
 * (loadBreachRows -> SECURITY INVOKER rpc, RLS-respecting). Presentation-only;
 * it composes the shared <Modal> (never modifies it) and resolves display
 * columns via the shared helpers so no raw surrogate id leads the view (§17.B4).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/common/Modal';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { EmptyState } from '../../components/common/EmptyState';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import {
  loadBreachRows,
  pickBreachColumns,
  ruleReferencedColumns,
  BREACH_ROWS_DEFAULT_LIMIT,
  type BreachRows,
} from '../../lib/dqe_breach_scan';

export interface DqeBreachReviewModalProps {
  ruleId: number;
  ruleCode: string;
  ruleType: string;
  ruleDef: unknown;
  onClose: () => void;
}

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function DqeBreachReviewModal({ ruleId, ruleCode, ruleType, ruleDef, onClose }: DqeBreachReviewModalProps) {
  const { t } = useTranslation();
  const fmtDate = useFormatDate();
  const fmtDateTime = useFormatDateTime();
  const [data, setData] = useState<BreachRows | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setBusy(true); setError(null);
    const { data: d, error: e } = await loadBreachRows(ruleId, BREACH_ROWS_DEFAULT_LIMIT);
    if (e) { setError(e.message); setData(null); }
    else setData(d);
    setBusy(false);
  };
  useEffect(() => { void load(); }, [ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const columns = useMemo(
    () => pickBreachColumns(rows, ruleReferencedColumns(ruleType, ruleDef)),
    [rows, ruleType, ruleDef],
  );

  const formatCell = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string') {
      if (ISO_DATETIME.test(value)) return fmtDateTime(value);
      if (ISO_DATE.test(value)) return fmtDate(value);
      return value;
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const atLimit = rows.length >= BREACH_ROWS_DEFAULT_LIMIT;

  return (
    <Modal
      title={t('dqe.breach.review_title', 'Breaching rows — {{code}}', { code: ruleCode })}
      onClose={onClose}
      width="2xl"
      footer={
        <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
          {t('common.close', 'Close')}
        </button>
      }
    >
      <div className="mb-2 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
        {data?.entity_name && (
          <span>{t('dqe.breach.entity', 'Entity')}: <span className="font-mono">{data.entity_name}</span></span>
        )}
        {!busy && data?.is_evaluable === 'Y' && (
          <span className="ml-auto">{t('dqe.breach.row_count', '{{count}} breaching', { count: rows.length })}{atLimit ? '+' : ''}</span>
        )}
      </div>

      {error && (
        <ErrorBanner
          message={t('dqe.breach.load_failed', "Couldn't load breaching rows.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void load()}
          details_label={t('common.details', 'Details')}
        />
      )}

      {busy && <div className="py-8 text-center text-csm-sm text-csm-watermark">{t('dqe.breach.loading', 'Loading breaching rows…')}</div>}

      {!busy && !error && data?.is_evaluable === 'N' && (
        <EmptyState variant="no-data"
          title={t('dqe.breach.not_evaluable', 'This rule cannot be evaluated against the current data (unknown table/column or unrecognised format).')} />
      )}

      {!busy && !error && data?.is_evaluable === 'Y' && rows.length === 0 && (
        <EmptyState variant="no-data" title={t('dqe.breach.none', 'No rows currently breach this rule.')} />
      )}

      {!busy && !error && data?.is_evaluable === 'Y' && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-csm-sm">
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr>
                {columns.map(col => (
                  <th key={col} className="border-b border-csm-border px-2 py-1 text-left font-mono">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-csm-surface-alt">
                  {columns.map(col => (
                    <td key={col} className="border-b border-csm-border-soft px-2 py-1">{formatCell(row[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {atLimit && (
            <p className="mt-2 text-csm-xs text-csm-ink-soft">
              {t('dqe.breach.capped', 'Showing the first {{limit}} breaching rows. Fix these and re-scan to see the rest.', { limit: BREACH_ROWS_DEFAULT_LIMIT })}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
